import { motion } from 'framer-motion';
import { useState } from 'react';
import { AlertTriangle, Clock, FileText, Target, Radio, TrendingDown, MapPin, Users, DollarSign, BarChart3, MessageSquare, Timer, RotateCcw, AlertCircle, Package, Tag, Box, Truck, CheckCircle, ShoppingCart, Activity, Percent } from 'lucide-react';

const SignalsSection = () => {
  const [hoveredSignal, setHoveredSignal] = useState(null);

  const signals = [
    { abbr: 'ODR', name: 'Order Defect Rate', description: 'Tracks negative feedback, A-to-Z claims, and chargebacks as a percentage of orders', value: '0.8%', status: 'green', icon: AlertTriangle },
    { abbr: 'LSR', name: 'Late Shipment Rate', description: 'Percentage of orders shipped after the expected ship date', value: '2.1%', status: 'green', icon: Clock },
    { abbr: 'POL', name: 'Policy Violations', description: 'Active violations of Amazon selling policies on your account', value: '3', status: 'red', icon: FileText },
    { abbr: 'A-Z', name: 'A-to-Z Claims', description: 'Buyer claims for orders not received or significantly different', value: '0.2%', status: 'green', icon: Target },
    { abbr: 'VTR', name: 'Valid Tracking Rate', description: 'Percentage of packages with valid tracking information', value: '98%', status: 'green', icon: Radio },
    { abbr: 'CR', name: 'Cancellation Rate', description: 'Seller-initiated cancellations before shipment confirmation', value: '1.8%', status: 'yellow', icon: TrendingDown },
    { abbr: 'GEO', name: 'Geographic Performance', description: 'Performance metrics broken down by customer location', value: 'Normal', status: 'green', icon: MapPin },
    { abbr: 'PER', name: 'Performance Notifications', description: 'Active warnings or alerts from Amazon about your account', value: '0', status: 'green', icon: Users },
    { abbr: 'BPC', name: 'Buyer-Product Contact', description: 'Customer inquiries about products before or after purchase', value: 'None', status: 'green', icon: DollarSign },
    { abbr: 'API', name: 'API Health', description: 'Status of your SP-API integrations and data feeds', value: 'Normal', status: 'green', icon: BarChart3 },
    { abbr: 'NFR', name: 'Negative Feedback Rate', description: 'Percentage of orders receiving negative seller feedback', value: '2.4%', status: 'green', icon: MessageSquare },
    { abbr: 'MRT', name: 'Message Response Time', description: 'Average time to respond to buyer messages', value: '4h', status: 'green', icon: Timer },
    { abbr: 'RDR', name: 'Return Dissatisfaction', description: 'Rate of negative customer experience during returns', value: '0.3%', status: 'green', icon: RotateCcw },
    { abbr: 'ISR', name: 'Intellectual Property', description: 'IP complaints and potential trademark/patent issues', value: '5 ASINs', status: 'red', icon: AlertCircle },
    { abbr: 'ASN', name: 'ASIN Suppression', description: 'Listings currently suppressed or at risk of suppression', value: '0', status: 'green', icon: Package },
    { abbr: 'BRD', name: 'Brand Registry', description: 'Brand registry status and protection alerts', value: 'None', status: 'green', icon: Tag },
  ];

  // Additional rotating signals with descriptions
  const rotatingSignals = [
    { abbr: 'IPI', name: 'IPI Score', description: 'Inventory Performance Index measuring inventory efficiency', icon: BarChart3 },
    { abbr: 'STR', name: 'Stranded Inventory', description: 'Inventory in FBA without active listings', icon: Box },
    { abbr: 'CSD', name: 'Carrier Scan Delay', description: 'Time gap between ship confirmation and first carrier scan', icon: Truck },
    { abbr: 'DPB', name: 'Delivery Promise Breach', description: 'Orders delivered after promised delivery date', icon: AlertCircle },
    { abbr: 'BBP', name: 'Buy Box Percentage', description: 'How often you win the Buy Box on your listings', icon: ShoppingCart },
    { abbr: 'SES', name: 'Session Rate', description: 'Number of visits to your product pages', icon: Activity },
    { abbr: 'CVR', name: 'Conversion Rate', description: 'Percentage of page visits that result in purchases', icon: Percent },
    { abbr: 'OTD', name: 'On-Time Delivery', description: 'Percentage of orders delivered by promised date', icon: CheckCircle },
    { abbr: 'INV', name: 'Inventory Age', description: 'How long inventory has been in FBA warehouses', icon: Clock },
    { abbr: 'REF', name: 'Refund Rate', description: 'Percentage of orders that are refunded', icon: RotateCcw },
    { abbr: 'VOC', name: 'Voice of Customer', description: 'Aggregated customer feedback and complaints', icon: MessageSquare },
    { abbr: 'AUT', name: 'Authenticity Claims', description: 'Claims that products are not genuine', icon: AlertTriangle },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'green': return 'bg-emerald-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  const getValueColor = (status) => {
    switch (status) {
      case 'green': return 'text-emerald-300';
      case 'yellow': return 'text-yellow-300';
      case 'red': return 'text-red-300';
      default: return 'text-slate-300';
    }
  };

  return (
    <section className="bg-slate-900 py-20 lg:py-24" data-testid="signals-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-emerald-400">
            What We Monitor
          </p>
          <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl mt-3">
            <span className="text-emerald-400 font-bold">50+</span>{' '}
            <span className="font-light">signals that predict account risk</span>
          </h2>
          <p className="text-slate-400 text-lg mt-4 max-w-2xl mx-auto">
            We track the metrics Amazon uses to evaluate your account health, plus leading indicators that predict where those metrics are heading.
          </p>
        </motion.div>

        {/* Signals Grid with Hover Tooltips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3"
        >
          {signals.map((signal, index) => (
            <motion.div
              key={signal.abbr}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.03 }}
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 relative group hover:border-emerald-500/50 hover:bg-slate-800 transition-all cursor-pointer"
              onMouseEnter={() => setHoveredSignal(signal.abbr)}
              onMouseLeave={() => setHoveredSignal(null)}
              data-testid={`signal-card-${signal.abbr}`}
            >
              {/* Hover Tooltip */}
              {hoveredSignal === signal.abbr && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-slate-950 border border-slate-700 rounded-lg shadow-xl"
                  data-testid={`tooltip-${signal.abbr}`}
                >
                  <p className="text-emerald-400 font-semibold text-sm mb-1">{signal.name}</p>
                  <p className="text-slate-300 text-xs leading-relaxed">{signal.description}</p>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                    <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-slate-950" />
                  </div>
                </motion.div>
              )}
              
              {/* Status dot */}
              <div className={`absolute top-3 right-3 w-2 h-2 rounded-full ${getStatusColor(signal.status)}`} />
              
              {/* Icon */}
              <signal.icon className="w-5 h-5 text-slate-400 group-hover:text-emerald-400 mb-2 transition-colors" strokeWidth={1.5} />
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">{signal.abbr}</p>
              
              {/* Value */}
              <p className={`text-lg font-bold mt-1 ${getValueColor(signal.status)}`}>
                {signal.value}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Rotating Signals Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-10 overflow-hidden relative"
          data-testid="rotating-signals-marquee"
        >
          {/* Gradient overlays for smooth fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none" />
          
          {/* Marquee container */}
          <div className="flex animate-marquee hover:pause-animation">
            {/* First set of signals */}
            {rotatingSignals.map((signal, index) => (
              <div
                key={`first-${signal.abbr}`}
                className="flex-shrink-0 mx-2 group relative"
                data-testid={`rotating-signal-${signal.abbr}`}
              >
                <div className="flex items-center gap-3 px-5 py-3 bg-slate-800/60 border border-slate-700 rounded-full hover:border-emerald-500/50 hover:bg-slate-800 transition-all cursor-pointer">
                  <signal.icon className="w-4 h-4 text-slate-400 group-hover:text-emerald-400 transition-colors" strokeWidth={1.5} />
                  <span className="text-sm text-slate-300 font-medium whitespace-nowrap">{signal.name}</span>
                </div>
                {/* Tooltip on hover */}
                <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-slate-950 border border-slate-700 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <p className="text-emerald-400 font-semibold text-xs mb-1">{signal.abbr}</p>
                  <p className="text-slate-300 text-xs leading-relaxed">{signal.description}</p>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                    <div className="w-0 h-0 border-l-6 border-r-6 border-t-6 border-transparent border-t-slate-950" />
                  </div>
                </div>
              </div>
            ))}
            {/* Duplicate set for seamless loop */}
            {rotatingSignals.map((signal, index) => (
              <div
                key={`second-${signal.abbr}`}
                className="flex-shrink-0 mx-2 group relative"
              >
                <div className="flex items-center gap-3 px-5 py-3 bg-slate-800/60 border border-slate-700 rounded-full hover:border-emerald-500/50 hover:bg-slate-800 transition-all cursor-pointer">
                  <signal.icon className="w-4 h-4 text-slate-400 group-hover:text-emerald-400 transition-colors" strokeWidth={1.5} />
                  <span className="text-sm text-slate-300 font-medium whitespace-nowrap">{signal.name}</span>
                </div>
                {/* Tooltip on hover */}
                <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-slate-950 border border-slate-700 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <p className="text-emerald-400 font-semibold text-xs mb-1">{signal.abbr}</p>
                  <p className="text-slate-300 text-xs leading-relaxed">{signal.description}</p>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                    <div className="w-0 h-0 border-l-6 border-r-6 border-t-6 border-transparent border-t-slate-950" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Plus more indicator */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-slate-500 font-mono text-sm mt-8"
        >
          Plus 30+ additional signals tracked in real-time
        </motion.p>
      </div>
    </section>
  );
};

export default SignalsSection;
